import { formatPriceAndCurrency } from '@common/utils'
import {
  MolsonAccordion,
  MolsonBox,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme'

import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'

interface BreakdownSectionProps {
  children: (NonNullable<ReactNode> & ReactNode) | null
  errorPrice?: boolean
  firstInList?: boolean
  highlightedSection?: boolean
  oldPrice?: number
  price?: number
  title: string
}

export const BreakdownSection = ({
  children,
  errorPrice,
  firstInList = false,
  highlightedSection = false,
  oldPrice,
  price,
  title
}: BreakdownSectionProps) => {
  const theme = molsonUseTheme()

  const { specId } = useParams()

  const displayOldPrice = oldPrice !== undefined && !specId
  const displayPrice = price !== undefined

  const getSummaryPadding = () => {
    if (highlightedSection) {
      return firstInList ? '0px 0px 24px' : '4px 0px 12px'
    }

    return firstInList ? '0px 0px 16px' : '4px 0px'
  }

  const priceTextLabel = displayPrice ? (
    <MolsonTypography
      sx={{ color: theme.palette.secondary[900] }}
      variant="subtitle2"
    >
      {formatPriceAndCurrency(price)}
    </MolsonTypography>
  ) : null

  const additionalSummaryComponent =
    displayOldPrice && displayPrice ? (
      <MolsonBox
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: oldPrice === price ? 1 : 0.5
        }}
      >
        <MolsonIcon
          variant="WARNING"
          iconColor="warning"
          fontSize="small"
          sx={{ mr: 0.5 }}
        />

        <MolsonTypography
          sx={{
            color: theme.palette.secondary[500],
            fontWeight: 500,
            textDecoration: 'line-through'
          }}
          variant="subtitle2"
        >
          {formatPriceAndCurrency(oldPrice)}
        </MolsonTypography>
        {(oldPrice > price || oldPrice < price) && (
          <MolsonIcon
            variant={price > oldPrice ? 'ARROW_UPWARD' : 'ARROW_DOWNWARD'}
            iconColor={price > oldPrice ? 'success' : 'error'}
            sx={{ fontSize: 16 }}
          />
        )}
        <MolsonTypography
          sx={{ color: theme.palette.secondary[900] }}
          variant="subtitle2"
        >
          {formatPriceAndCurrency(price)}
        </MolsonTypography>
      </MolsonBox>
    ) : (
      priceTextLabel
    )

  return (
    <MolsonBox
      sx={{
        '&:last-of-type .MuiAccordion-root:after': {
          display: 'none'
        },
        '.MuiAccordionSummary-content': {
          '& > div:first-of-type': {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap'
          },
          margin: firstInList ? '0px' : '12px 0px'
        },
        backgroundColor: theme.palette.secondary[50],
        mb: 0,
        pb: highlightedSection ? 1 : 0,
        px: 4
      }}
    >
      <MolsonAccordion
        primaryTextSx={{
          color: theme.palette.secondary[900],
          fontWeight: 700
        }}
        summarySx={{
          padding: getSummaryPadding()
        }}
        sx={{
          '& .MuiAccordionSummary-content > div h6:nth-of-type(2)': {
            color: errorPrice ? theme?.palette.primary.error : 'currentColor'
          },
          '& .MuiButtonBase-root.MuiAccordionSummary-root': {
            minHeight: 0
          },
          '&:after': {
            backgroundColor: highlightedSection
              ? theme.palette.secondary[300]
              : theme.palette.secondary[200],
            display: 'block'
          },
          backgroundColor: theme.palette.secondary[50]
        }}
        additionalSummaryComponent={additionalSummaryComponent}
        primaryText={title}
        primaryTextVariant="subtitle2"
        size="sm"
      >
        <MolsonBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {children}
        </MolsonBox>
      </MolsonAccordion>
    </MolsonBox>
  )
}
